import React, { useRef, useState } from 'react';
import { X, Download, Printer, Edit, CheckCircle, XCircle, Send } from 'lucide-react';
import Button from '../../../components/ui/Button';
import { formatINR } from '../../../utils/formatIndianCurrency';
import { useAuth } from '../../../contexts/AuthContext';

const InvoiceTemplate = ({ invoice, isOpen, onClose, onEdit, onApprove, onReject, onSend }) => {
  const invoiceRef = useRef(null);
  const { userProfile } = useAuth();

  // Determine button visibility based on status and role
  const isSystemAdmin = userProfile?.role === 'system_admin';
  const invoiceStatus = invoice?.status?.toLowerCase() || 'pending';
  
  // Button visibility logic - restrict all actions for paid invoices
  const isPaidInvoice = invoiceStatus === 'paid';
  
  const showApproveButton = !isPaidInvoice && invoiceStatus === 'pending';
  // CRITICAL FIX: For approved invoices, only system_admin can reject
  const showRejectButton = !isPaidInvoice && invoiceStatus === 'approved' && isSystemAdmin;
  // Edit button only for pending invoices  
  const showEditButton = !isPaidInvoice && invoiceStatus === 'pending';
  
  // Send button only for approved invoices
  const showSendButton = !isPaidInvoice && invoiceStatus === 'approved';

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    // In production, integrate with a PDF library like jsPDF or react-pdf
    alert('PDF download functionality would be integrated here');
  };

  const handleEdit = () => {
    // When editing, status changes to pending
    const updatedInvoice = { ...invoice, status: 'pending' };
    onEdit?.(updatedInvoice);
  };

  const handleApprove = () => {
    if (window.confirm(`Are you sure you want to approve invoice ${invoice?.id}? This action cannot be undone.`)) {
      onApprove?.(invoice);
      onClose?.();
    }
  };

  const handleReject = () => {
    if (window.confirm(`Are you sure you want to reject invoice ${invoice?.id}? This action cannot be undone.`)) {
      onReject?.(invoice);
      onClose?.();
    }
  };

  const handleSend = () => {
    if (window.confirm(`Send invoice ${invoice?.id} to customer? This will email the invoice to ${invoice?.client}.`)) {
      onSend?.(invoice);
      alert(`✅ Invoice ${invoice?.id} sent successfully to ${invoice?.client}!`);
    }
  };

  if (!isOpen || !invoice) return null;

  const supplierInfo = {
    name: 'Right Vectors Australia',
    address: 'Unit 1405, 280 Spencer Street',
    city: 'Melbourne, Victoria, AUSTRALIA 3000',
    abn: '81 161 310 792'
  };

  const customerInfo = {
    name: 'RNR Steels and Metals',
    address: '2-1-53 574, Ramalayam Temple Back Side',
    city: 'Vangayagudem, Eluru, West Godavari-534001',
    state: 'Andhra Pradesh, INDIA 543001',
    iec: 'ABGFR8672J'
  };

  // Calculate GST (10% of AUD amount)
  const audSubtotal = invoice?.amountAUD || 0;
  const gstAmount = audSubtotal * 0.10;
  const audTotal = audSubtotal + gstAmount;

  // Status badge configuration for prominent display
  const getStatusConfig = (status) => {
    const configs = {
      pending: {
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800',
        borderColor: 'border-yellow-300',
        icon: '⏳',
        label: 'Pending',
        description: 'Awaiting approval'
      },
      approved: {
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-800',
        borderColor: 'border-blue-300',
        icon: '✅',
        label: 'Approved',
        description: 'Ready to send'
      },
      paid: {
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
        borderColor: 'border-green-300',
        icon: '💰',
        label: 'Paid',
        description: 'Payment received'
      },
      overdue: {
        bgColor: 'bg-red-100',
        textColor: 'text-red-800',
        borderColor: 'border-red-300',
        icon: '⚠️',
        label: 'Overdue',
        description: 'Payment past due date'
      }
    };
    return configs?.[status] || configs?.pending;
  };

  const statusConfig = getStatusConfig(invoiceStatus);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-5xl w-full max-h-[95vh] overflow-y-auto">
        {/* Modal Header with Actions - Redesigned for Mobile */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-3 sm:px-6 py-3 sm:py-4 print:hidden">
          {/* Title and Close Button Row */}
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900">Invoice Preview</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* PROMINENT STATUS DISPLAY - NEW FEATURE */}
          <div className={`${statusConfig?.bgColor} ${statusConfig?.borderColor} border-2 rounded-xl p-4 mb-4 shadow-sm`}>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <span className="text-3xl">{statusConfig?.icon}</span>
                <div>
                  <h3 className={`text-xl font-bold ${statusConfig?.textColor}`}>
                    {statusConfig?.label}
                  </h3>
                  <p className={`text-sm ${statusConfig?.textColor} opacity-80`}>
                    {statusConfig?.description}
                  </p>
                </div>
              </div>
              <div className={`${statusConfig?.bgColor} border ${statusConfig?.borderColor} rounded-lg px-4 py-2`}>
                <p className={`text-xs font-semibold ${statusConfig?.textColor} uppercase tracking-wide`}>
                  Invoice Status
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons Row - Conditional Visibility Based on Status and Role */}
          <div className="flex items-center gap-2 flex-wrap">
            {/* Send Button - Only for Approved status */}
            {showSendButton && (
              <button
                onClick={handleSend}
                className="flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-3 sm:px-4 py-2 rounded-lg transition-colors min-h-[44px] flex-1 sm:flex-initial"
                aria-label="Send Invoice"
                title="Send Invoice"
              >
                <Send className="h-5 w-5" />
                <span className="hidden sm:inline font-medium">Send</span>
              </button>
            )}

            {/* Approve Button - Only for Pending status */}
            {showApproveButton && (
              <button
                onClick={handleApprove}
                className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-3 sm:px-4 py-2 rounded-lg transition-colors min-h-[44px] flex-1 sm:flex-initial"
                aria-label="Approve Invoice"
                title="Approve Invoice"
              >
                <CheckCircle className="h-5 w-5" />
                <span className="hidden sm:inline font-medium">Approve</span>
              </button>
            )}
            
            {/* Edit Button - For Pending status only */}
            {showEditButton && (
              <button
                onClick={handleEdit}
                className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-3 sm:px-4 py-2 rounded-lg transition-colors min-h-[44px] flex-1 sm:flex-initial"
                aria-label="Edit Invoice"
                title="Edit Invoice"
              >
                <Edit className="h-5 w-5" />
                <span className="hidden sm:inline font-medium">Edit</span>
              </button>
            )}

            {/* Reject Button - For Approved (system_admin only) and Pending status */}
            {showRejectButton && (
              <button
                onClick={handleReject}
                className="flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-3 sm:px-4 py-2 rounded-lg transition-colors min-h-[44px] flex-1 sm:flex-initial"
                aria-label="Reject Invoice"
                title="Reject Invoice"
              >
                <XCircle className="h-5 w-5" />
                <span className="hidden sm:inline font-medium">Reject</span>
              </button>
            )}
            
            {/* Secondary Action Buttons - Download and Print */}
            <button
              onClick={handleDownload}
              className="flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 sm:px-4 py-2 rounded-lg transition-colors min-h-[44px]"
              aria-label="Download"
              title="Download"
            >
              <Download className="h-5 w-5" />
              <span className="hidden sm:inline">Download</span>
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 sm:px-4 py-2 rounded-lg transition-colors min-h-[44px] hidden sm:flex"
              aria-label="Print"
              title="Print"
            >
              <Printer className="h-5 w-5" />
              <span className="hidden sm:inline">Print</span>
            </button>
          </div>
        </div>

        {/* Invoice Content */}
        <div ref={invoiceRef} className="p-4 sm:p-8 bg-white">
          {/* Letterhead with Logo - REORGANIZED LAYOUT */}
          <div className="border-b-4 border-blue-600 pb-6 mb-8">
            <div className="flex flex-col sm:flex-row items-start gap-6">
              {/* Logo - Left Side */}
              <div className="w-full sm:w-32 flex justify-center sm:justify-start flex-shrink-0">
                <div className="w-32 h-32 sm:w-24 sm:h-24 flex items-center justify-center">
                  <img 
                    src="/assets/images/RVA2Logo-Var_1_-1765668587384.png" 
                    alt="Right Vectors Australia Logo - Professional coal trading management services company"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>

              {/* Company Info - Right Column with Left Justification */}
              <div className="flex-1 w-full">
                <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                  {/* Company Name and Address - Left Justified */}
                  <div className="flex-1">
                    <h1 className="text-2xl sm:text-3xl font-bold text-blue-900 text-left">{supplierInfo?.name}</h1>
                    <div className="mt-3 text-gray-600 text-sm space-y-1 text-left">
                      <p className="font-medium">Registered Office:</p>
                      <p>{supplierInfo?.address}</p>
                      <p>{supplierInfo?.city}</p>
                      <p className="mt-2">
                        <span className="font-medium">Australian Business Number:</span> {supplierInfo?.abn}
                      </p>
                    </div>
                  </div>

                  {/* Invoice Number Badge */}
                  <div className="text-center sm:text-right w-full sm:w-auto">
                    <div className="bg-blue-600 text-white px-6 py-3 rounded-lg inline-block">
                      <p className="text-sm font-medium">INVOICE</p>
                      <p className="text-2xl font-bold mt-1">{invoice?.id}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Invoice Details */}
          <div className="grid grid-cols-2 gap-8 mb-8">
            {/* Bill To */}
            <div>
              <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wide">Bill To:</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="font-bold text-gray-900 text-lg">{customerInfo?.name}</p>
                <div className="mt-2 text-gray-600 text-sm space-y-1">
                  <p className="font-medium">Registered Office:</p>
                  <p>{customerInfo?.address}</p>
                  <p>{customerInfo?.city}</p>
                  <p>{customerInfo?.state}</p>
                  <p className="mt-2">
                    <span className="font-medium">Importer-Exporter Code:</span> {customerInfo?.iec}
                  </p>
                </div>
              </div>
            </div>

            {/* Invoice Information */}
            <div>
              <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wide">Invoice Details:</h3>
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span className="text-gray-600 font-medium">Invoice Date:</span>
                  <span className="text-gray-900 font-semibold">
                    {new Date(invoice?.invoiceDate)?.toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span className="text-gray-600 font-medium">Due Date:</span>
                  <span className="text-gray-900 font-semibold">
                    {new Date(invoice?.dueDate)?.toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span className="text-gray-600 font-medium">Shipment Reference:</span>
                  <span className="text-blue-600 font-semibold">{invoice?.shipmentRef}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-gray-600 font-medium">Payment Terms:</span>
                  <span className="text-gray-900 font-semibold">{invoice?.paymentTerms}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Services Table */}
          <div className="mb-8">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-100 border-y-2 border-gray-300">
                  <th className="text-left py-3 px-4 font-bold text-gray-900">Description</th>
                  <th className="text-right py-3 px-4 font-bold text-gray-900">Quantity</th>
                  <th className="text-right py-3 px-4 font-bold text-gray-900">Rate (USD)</th>
                  <th className="text-right py-3 px-4 font-bold text-gray-900">Amount (USD)</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-200">
                  <td className="py-4 px-4">
                    <p className="font-medium text-gray-900">{invoice?.description || 'Coal Trading Management Services'}</p>
                    <p className="text-sm text-gray-600 mt-1">
                      Professional management and coordination services for coal shipment operations
                    </p>
                  </td>
                  <td className="text-right py-4 px-4 text-gray-900">
                    {invoice?.tonnage?.toLocaleString()} MT
                  </td>
                  <td className="text-right py-4 px-4 text-gray-900">
                    ${invoice?.feeRate?.toFixed(2)}
                  </td>
                  <td className="text-right py-4 px-4 font-semibold text-gray-900">
                    ${invoice?.amountUSD?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Totals Section */}
          <div className="flex justify-end mb-8">
            <div className="w-96">
              {/* USD Section */}
              <div className="mb-4 pb-4 border-b-2 border-gray-200">
                <div className="flex justify-between py-2">
                  <span className="text-gray-700 font-medium">Subtotal (USD):</span>
                  <span className="text-gray-900 font-semibold">
                    ${invoice?.amountUSD?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="bg-blue-50 p-3 rounded-lg mt-2">
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-blue-900">Total (USD):</span>
                    <span className="font-bold text-xl text-blue-900">
                      ${invoice?.amountUSD?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              </div>

              {/* AUD Section - PRIMARY (Australian Company) */}
              <div className="mb-4 pb-4 border-b-2 border-gray-200">
                <div className="bg-amber-50 p-3 rounded-lg mb-3 border border-amber-200">
                  <p className="text-xs font-semibold text-amber-800 flex items-center gap-1">
                    <span className="text-base">🇦🇺</span>
                    PRIMARY INVOICE CURRENCY (Australian Company)
                  </p>
                  <p className="text-xs text-amber-700 mt-1">
                    Exchange Rate: 1 USD = A$1.52
                  </p>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between py-2">
                    <span className="text-gray-700 font-medium">Subtotal (AUD):</span>
                    <span className="text-gray-900 font-semibold">
                      A${audSubtotal?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 border-t border-gray-200">
                    <span className="text-gray-700 font-medium">GST (10%):</span>
                    <span className="text-gray-900 font-semibold">
                      A${gstAmount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
                <div className="bg-green-600 text-white p-4 rounded-lg mt-3">
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-lg">TOTAL DUE (AUD inc. GST):</span>
                    <span className="font-bold text-2xl">
                      A${audTotal?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                  <p className="text-xs text-green-100 mt-2">
                    * Final payable amount including 10% GST
                  </p>
                </div>
              </div>

              {/* INR Conversion with Indian Number Format */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-300">
                <p className="text-xs text-gray-600 mb-3">
                  Exchange Rate: 1 USD = ₹{invoice?.exchangeRate}
                </p>
                <div className="space-y-2">
                  <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                    <span className="font-bold text-gray-900">Amount in INR:</span>
                    <span className="font-bold text-xl text-gray-900">
                      {formatINR(invoice?.amountINR)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 italic mt-2">
                    * Formatted in Indian numbering system (Crores, Lakhs)
                  </p>
                  <p className="text-xs text-gray-500 italic">
                    * Reference only - Payment due in AUD
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Instructions */}
          <div className="bg-blue-50 p-6 rounded-lg border border-blue-200 mb-8">
            <h3 className="font-bold text-gray-900 mb-3">Payment Instructions:</h3>
            <div className="text-sm text-gray-700 space-y-1">
              <p className="font-semibold text-green-700 mb-2">
                💰 Please make payment in AUD (Australian Dollars) - Total: A${audTotal?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
              <p>Please make payment to the following account:</p>
              <p className="font-medium mt-2">Bank Details would be added here</p>
              <p className="text-xs text-gray-600 mt-3">
                Please include invoice number {invoice?.id} in the payment reference
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t-2 border-gray-300 pt-6 text-center text-sm text-gray-600">
            <p className="font-medium mb-2">Thank you for your business!</p>
            <p className="text-xs">
              This is a computer-generated invoice and does not require a signature.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceTemplate;